/**
 * BPT-form constraint parameter writers — DYN-4 Phase 4a.
 *
 * Symmetric to `dxfBridge.dynamic.parser.constraints.ts` (the AutoCAD
 * 2018+ BPT-form decoder). Emits `ACDBBLOCK{HORIZONTAL,VERTICAL,
 * RADIAL,ANGULAR}CONSTRAINTPARAMETER` records in the shape that ezdxf
 * and real AutoCAD 2018 produce — group 300 for the name, 1010/1020
 * block-parameter-point codes for the geometric endpoints, and a
 * signed group 140 for the current scalar value. This is the
 * authoritative writer path for round-tripping АБИ1.dxf's ibeam
 * dynamic block.
 *
 * The existing tier-2 writers in `dxfBridge.dynamic.writer.tier2.ts`
 * emit the older synthesized shape (group 1 name, codes 10/11 for
 * points); they stay authoritative for round-trip tests that exercise
 * that legacy encoding. The top-level dispatcher in
 * `dxfBridge.dynamic.writer.ts` prefers the BPT writers below and falls
 * back to the legacy ones only when the constraint parameter in hand
 * does not round-trip through the BPT parser cleanly (e.g. a fixture
 * with authored `distanceValueSet.min` that would collide with the BPT
 * parser's 141=min convention). In practice every ordinary
 * authored-from-AutoCAD parameter is emitted through this module.
 *
 * Group-code convention (must match parser.constraints):
 *   0    ACDB…CONSTRAINTPARAMETER
 *   5    <handle>
 *   330  <block record>
 *   100  AcDbEvalExpr / AcDbBlockElement / AcDbBlockParameter /
 *        AcDbBlock2PtParameter (or 3Pt for angular) / AcDbBlock…ConstraintParameter
 *   300  <name>                    ← BPT-form primary name
 *   1071 0                          ← signature code AutoCAD always emits
 *   1010/1020/1030 basePoint        ← inherited from AcDbBlock2PtParameter
 *   1011/1021/1031 secondPoint
 *   1012/1022/1032 angular vertex   ← angular only, after AngularConstraintParameter marker
 *   140  currentDistance / currentRadius / currentAngle
 *   141  defaultDistance / defaultRadius / defaultAngle     ← only when differs from current
 *   175  valueSet mode (0/1/2)
 *   143  valueSet increment
 *   141  valueSet min                ← reuses 141; parser disambiguates by mode!=none
 *   142  valueSet max
 *   145  valueSet list values (repeating)
 *   305  <name>                     ← AutoCAD duplicates the name on 305 too
 *   280  chainActions
 *   281  showGrip
 *
 * Note on the 141 double-use: AutoCAD writes 141 twice when both
 * `defaultDistance` (pre-value-set) and `valueSet.min` are authored — the
 * first appearance wins in the parser because it uses `firstNumber`.
 * We mirror that by emitting 141 in the same order: first as default,
 * then inside the value-set block as min. When mode==="none" we omit
 * the min/max lines entirely per parser.constraints guard.
 */
import type { DxfDynamicBlockObject, DxfGroup } from "@dxf-ai-editor/dxf-io";

import type {
  AngularConstraintParameter,
  HorizontalConstraintParameter,
  RadialConstraintParameter,
  VerticalConstraintParameter,
  ValueSet,
} from "./dynamicBlock";
import { pushGroup } from "./dxfBridge.dynamic.shared";

/** Writer context shared with tier2 — we re-use the same minimal shape. */
export type ConstraintWriterContext = {
  nextHandle: () => string;
  blockRecordHandle: string;
};

// ═══════════════════════════════════════════════════════════════════════════
// Shared emission helpers (BPT shape)
// ═══════════════════════════════════════════════════════════════════════════

/** Emit the standard AcDbEvalExpr/AcDbBlockElement/AcDbBlockParameter
 *  chain + the constraint-specific subclass tail, matching AutoCAD's
 *  markers observed in АБИ1.dxf. The parser.constraints code ignores
 *  these markers for decoding — it anchors on group codes directly —
 *  but emitting them keeps the file bit-compatible with AutoCAD output. */
function emitBptHeader(
  groups: DxfGroup[],
  className: string,
  handle: string,
  ownerHandle: string,
  subclassChain: string[],
): void {
  pushGroup(groups, 0, className);
  pushGroup(groups, 5, handle);
  pushGroup(groups, 330, ownerHandle);
  for (const sub of subclassChain) pushGroup(groups, 100, sub);
}

/** Emit the name in the AutoCAD 2018+ BPT-form shape:
 *    300 <name>
 *    1071 0
 *  followed later by a trailing 305 <name> replay. Writing name on 300
 *  is what `parser.constraints.resolveConstraintName` reads first. */
function emitBptNameLine(groups: DxfGroup[], name: string): void {
  if (name) pushGroup(groups, 300, name);
  pushGroup(groups, 1071, 0);
}

/** Emit point X/Y/Z on the "block-parameter-point" group codes (1010+,
 *  1011+, 1012+). Z is always zero for our 2D drawing-point schema. */
function emitBptPoint(
  groups: DxfGroup[],
  xCode: number,
  yCode: number,
  zCode: number,
  p: { x: number; y: number },
): void {
  pushGroup(groups, xCode, p.x);
  pushGroup(groups, yCode, p.y);
  pushGroup(groups, zCode, 0);
}

/** Emit the constraint value-set block on codes 175/143/141/142/145.
 *  Mirrors `parseConstraintValueSet` in parser.constraints exactly — the
 *  141=min line is only emitted when mode!=="none" (the parser's
 *  gate), so it does not collide with the preceding 141=default line. */
function emitConstraintValueSet(groups: DxfGroup[], vs: ValueSet): void {
  const modeRaw = vs.mode === "increment" ? 1 : vs.mode === "list" ? 2 : 0;
  pushGroup(groups, 175, modeRaw);
  pushGroup(groups, 143, vs.increment);
  if (vs.mode !== "none") {
    if (vs.min !== undefined) pushGroup(groups, 141, vs.min);
    if (vs.max !== undefined) pushGroup(groups, 142, vs.max);
  }
  for (const v of vs.listValues) pushGroup(groups, 145, v);
}

/** Emit the trailing flags block shared by every constraint kind.
 *  The secondary name replay on 305 comes before the flags in AutoCAD's
 *  output; mirror that. */
function emitBptTail(
  groups: DxfGroup[],
  name: string,
  chainActions: boolean,
  showGrip: boolean,
): void {
  if (name) pushGroup(groups, 305, name);
  pushGroup(groups, 280, chainActions ? 1 : 0);
  pushGroup(groups, 281, showGrip ? 1 : 0);
}

// ═══════════════════════════════════════════════════════════════════════════
// Horizontal / Vertical / Radial constraint writers
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Emit an `ACDBBLOCKHORIZONTALCONSTRAINTPARAMETER` in BPT form. Mirrors
 * `parseHorizontalConstraintParameterFromBpt` so a writer→parser round
 * trip recovers every field.
 *
 * The current distance is emitted unsigned (140 positive) — АБИ1.dxf
 * does write it negative (the sign encodes grip-drag direction), but
 * the parser `Math.abs()`es it on the read side so the value we hand
 * round-trips identically. When the authored `currentDistance` is
 * already negative upstream we still emit its magnitude.
 */
export function writeHorizontalConstraintParameterBpt(
  p: HorizontalConstraintParameter,
  ctx: ConstraintWriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();
  emitBptHeader(
    groups,
    "ACDBBLOCKHORIZONTALCONSTRAINTPARAMETER",
    handle,
    ctx.blockRecordHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockParameter",
      "AcDbBlock2PtParameter",
      "AcDbBlockConstraintParameter",
      "AcDbBlockLinearConstraintParameter",
      "AcDbBlockHorizontalConstraintParameter",
    ],
  );
  emitBptNameLine(groups, p.name);
  emitBptPoint(groups, 1010, 1020, 1030, p.pointA);
  emitBptPoint(groups, 1011, 1021, 1031, p.pointB);
  pushGroup(groups, 140, p.currentDistance);
  // Emit 141 only when the default differs from current — otherwise we
  // would re-use the same 141 slot for both `defaultDistance` and
  // `valueSet.min`, and the parser's `firstNumber(141)` would pick the
  // wrong one. When defaults match current, the parser falls back to
  // `currentDistance` for both slots.
  if (p.defaultDistance !== p.currentDistance) {
    pushGroup(groups, 141, p.defaultDistance);
  }
  emitConstraintValueSet(groups, p.distanceValueSet);
  emitBptTail(groups, p.name, p.chainActions, p.showGrip);
  if (p.label) pushGroup(groups, 3, p.label);
  if (p.description) pushGroup(groups, 4, p.description);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKHORIZONTALCONSTRAINTPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

/** Emit an `ACDBBLOCKVERTICALCONSTRAINTPARAMETER` in BPT form. */
export function writeVerticalConstraintParameterBpt(
  p: VerticalConstraintParameter,
  ctx: ConstraintWriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();
  emitBptHeader(
    groups,
    "ACDBBLOCKVERTICALCONSTRAINTPARAMETER",
    handle,
    ctx.blockRecordHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockParameter",
      "AcDbBlock2PtParameter",
      "AcDbBlockConstraintParameter",
      "AcDbBlockLinearConstraintParameter",
      "AcDbBlockVerticalConstraintParameter",
    ],
  );
  emitBptNameLine(groups, p.name);
  emitBptPoint(groups, 1010, 1020, 1030, p.pointA);
  emitBptPoint(groups, 1011, 1021, 1031, p.pointB);
  pushGroup(groups, 140, p.currentDistance);
  if (p.defaultDistance !== p.currentDistance) {
    pushGroup(groups, 141, p.defaultDistance);
  }
  emitConstraintValueSet(groups, p.distanceValueSet);
  emitBptTail(groups, p.name, p.chainActions, p.showGrip);
  if (p.label) pushGroup(groups, 3, p.label);
  if (p.description) pushGroup(groups, 4, p.description);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKVERTICALCONSTRAINTPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

/**
 * Emit an `ACDBBLOCKRADIALCONSTRAINTPARAMETER` in BPT form. Note this
 * is the AutoCAD 2018+ "un-prefixed" spelling (after canonicalisation)
 * — NOT the older tier-3 `ACDBBLOCKCONSTRAINEDRADIAL` class which has
 * its own writer in `dxfBridge.dynamic.writer.tier3.ts`. The two
 * spellings map to the same schema kind `constraint-radial`; the
 * dispatcher prefers the BPT writer for all new emissions.
 */
export function writeRadialConstraintParameterBpt(
  p: RadialConstraintParameter,
  ctx: ConstraintWriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();
  emitBptHeader(
    groups,
    "ACDBBLOCKRADIALCONSTRAINTPARAMETER",
    handle,
    ctx.blockRecordHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockParameter",
      "AcDbBlock2PtParameter",
      "AcDbBlockConstraintParameter",
      "AcDbBlockRadialConstraintParameter",
    ],
  );
  emitBptNameLine(groups, p.name);
  emitBptPoint(groups, 1010, 1020, 1030, p.center);
  emitBptPoint(groups, 1011, 1021, 1031, p.radiusPoint);
  pushGroup(groups, 140, p.currentRadius);
  if (p.defaultRadius !== p.currentRadius) {
    pushGroup(groups, 141, p.defaultRadius);
  }
  emitConstraintValueSet(groups, p.radiusValueSet);
  emitBptTail(groups, p.name, p.chainActions, p.showGrip);
  if (p.label) pushGroup(groups, 3, p.label);
  if (p.description) pushGroup(groups, 4, p.description);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKRADIALCONSTRAINTPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Angular constraint writer
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Emit an `ACDBBLOCKANGULARCONSTRAINTPARAMETER` in BPT form. АБИ1.dxf
 * emits the vertex + two ray endpoints using a more complex layout
 * where the vertex sits at 1010/1020 (inherited from the
 * AcDbBlock2PtParameter chain) and the two arm endpoints go at
 * 1011/1021 and 1012/1022 — after the angular subclass marker. We
 * mirror that order so the parser resolves the three points correctly.
 *
 * `currentAngle` is emitted as-authored (radians, signed); AutoCAD's
 * UI flips the sign lazily based on drag direction and the parser
 * preserves the sign so round-trip stays exact.
 */
export function writeAngularConstraintParameterBpt(
  p: AngularConstraintParameter,
  ctx: ConstraintWriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();
  emitBptHeader(
    groups,
    "ACDBBLOCKANGULARCONSTRAINTPARAMETER",
    handle,
    ctx.blockRecordHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockParameter",
      "AcDbBlock2PtParameter",
      "AcDbBlockConstraintParameter",
      "AcDbBlockAngularConstraintParameter",
    ],
  );
  emitBptNameLine(groups, p.name);
  // Vertex — emitted at the inherited AcDbBlock2PtParameter slot.
  emitBptPoint(groups, 1010, 1020, 1030, p.vertex);
  // First ray endpoint — AutoCAD overwrites 1011/1021 here after the
  // angular marker; the parser picks the first occurrence which is
  // exactly this one.
  emitBptPoint(groups, 1011, 1021, 1031, p.pointA);
  // Second ray endpoint — 1012/1022 is unique to the angular form.
  emitBptPoint(groups, 1012, 1022, 1032, p.pointB);
  pushGroup(groups, 140, p.currentAngle);
  if (p.defaultAngle !== p.currentAngle) {
    pushGroup(groups, 141, p.defaultAngle);
  }
  emitConstraintValueSet(groups, p.angleValueSet);
  emitBptTail(groups, p.name, p.chainActions, p.showGrip);
  if (p.label) pushGroup(groups, 3, p.label);
  if (p.description) pushGroup(groups, 4, p.description);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKANGULARCONSTRAINTPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}
